import {Car, User, WorkingTimeType} from "@/shared/timr";
import {BreakTimeRecordingMode, WorkingTimeTypeCategory} from "@/shared/timrTypes";

export class DOMUtil {

	public static fillUserSelect(userSelect: JQuery,
								 users: User[],
								 undefinedOption: boolean | [string, string] = true) {
		DOMUtil.fillSelect(userSelect, users, undefinedOption, (item: User) => {
			return DOMUtil.createOption(String(item.id), item.fullname);
		});
	}

	public static fillCarSelect(carSelect: JQuery,
								cars: Car[],
								undefinedOption: boolean | [string, string] = true) {
		DOMUtil.fillSelect(carSelect, cars, undefinedOption, (item: Car) => {
			return DOMUtil.createOption(String(item.id), item.plate + " - " + item.name);
		});
	}

	/**
	 * Fills the working time type select with the given working time types grouped by their category.
	 * @param workingTimeTypeSelect The select element to fill
	 * @param workingTimeTypes 		The working time types to fill the select with
	 * @param undefinedOption 		Whether to add an undefined option
	 */
	public static fillWorkingTimeTypeSelect(workingTimeTypeSelect: JQuery,
											workingTimeTypes: WorkingTimeType[],
											undefinedOption: boolean | [string, string] = true) {
		const workingTimeTypeCategories = workingTimeTypes.reduce((map, wtt) => {
			if (!map[wtt.category]) {
				map[wtt.category] = [];
			}
			map[wtt.category]?.push(wtt);
			return map;
		}, {} as Record<WorkingTimeTypeCategory, WorkingTimeType[]>);

		DOMUtil.fillWorkingTimeTypeSelectByCategories(workingTimeTypeSelect, workingTimeTypeCategories, undefinedOption, (item: WorkingTimeType) => {
			return DOMUtil.createOption(String(item.id), item.name, {key: "workingTimeType", value: item});
		});
	}

	/**
	 * Fills the working time type select with the given working time types grouped by their category,
	 * and <b>adds the working time type categories as options.</b>
	 * @param workingTimeTypeSelect	The select element to fill
	 * @param workingTimeTypes 		The working time types to fill the select with
	 */
	public static fillWorkingTimeTypeReportSelect(workingTimeTypeSelect: JQuery, workingTimeTypes: WorkingTimeType[]) {
		DOMUtil.fillWorkingTimeTypeSelect(workingTimeTypeSelect, workingTimeTypes, ["undefined", messages["filter.all"]]);

		workingTimeTypeSelect.append(DOMUtil.createSeparator());
		workingTimeTypeSelect.append(DOMUtil.createOption(WorkingTimeTypeCategory.AttendanceTime, messages["workingTimeTypeCategory.ATTENDANCE_TIME"]));
		workingTimeTypeSelect.append(DOMUtil.createOption(WorkingTimeTypeCategory.Vacation, messages["workingTimeTypeCategory.VACATION"]));
		workingTimeTypeSelect.append(DOMUtil.createOption(WorkingTimeTypeCategory.PaidTimeOff, messages["workingTimeTypeCategory.PAID_TIME_OFF"]));
		workingTimeTypeSelect.append(DOMUtil.createOption(WorkingTimeTypeCategory.UnpaidTimeOff, messages["workingTimeTypeCategory.UNPAID_TIME_OFF"]));
		if (globals.breakTimeRecordingMode === BreakTimeRecordingMode.RECORD_WITH_POSITION) {
			workingTimeTypeSelect.append(DOMUtil.createOption(WorkingTimeTypeCategory.BreakTime, messages["workingTimeTypeCategory.BREAK_TIME"]));
		}
		workingTimeTypeSelect.append(DOMUtil.createOption(WorkingTimeTypeCategory.Suspension, messages["workingTimeTypeCategory.SUSPENSION"]));
	}

	public static fillSelect<T>(select: JQuery,
								items: T[],
								undefinedOption: boolean | [string, string],
								createOptionItemCallback: (item: T) => JQuery,
								clearSelect: boolean = true,
								keepCurrentSelection: boolean = true,
								value?: string) {
		const currentSelection = select.val() as string;
		if (clearSelect) {
			select.empty();
		}
		this.addUndefinedOption(select, undefinedOption);
		if (items) {
			$.each(items, (index: number, item: T) => {
				select.append(createOptionItemCallback(item));
			});
			if (keepCurrentSelection && currentSelection && this.hasOption(select, currentSelection)) {
				select.val(currentSelection);
			} else if (value) {
				select.val(value);
			} else {
				select.val(select.find("option:first").val() as string);
			}
		}
	}

	public static createOption<T extends string | number | boolean | symbol | object>(value: string, text: string, data?: { key: string, value: T }): JQuery {
		const option = $("<option></option>", {value: value}).text(text);
		if (data) {
			option.data(data.key, data.value);
		}
		return option;
	}

	public static createSeparator(): JQuery {
		return $("<option></option>", {disabled: true}).text("---");
	}

	public static hasOption(select: JQuery, value: string): boolean {
		return select.find(`option[value="${value}"]`).length > 0;
	}

	/**
	 * Fills the working time type select with the given working time types grouped by their category and orders them by the given order.
	 * @param select 					The select element to fill
	 * @param items 					The working time types to fill the select with
	 * @param undefinedOption 			Whether to add an undefined option
	 * @param createOptionItemCallback 	The callback to create the option item
	 * @param order 					The order of the working time type categories<br>
	 * 									Default order: [AttendanceTime, Vacation, PaidTimeOff, UnpaidTimeOff, BreakTime, Suspension]
	 * @private
	 */
	private static fillWorkingTimeTypeSelectByCategories(select: JQuery,
														 items: Record<WorkingTimeTypeCategory, WorkingTimeType[]>,
														 undefinedOption: boolean | [string, string],
														 createOptionItemCallback: (item: WorkingTimeType) => JQuery,
														 order: WorkingTimeTypeCategory[] = [
															 WorkingTimeTypeCategory.AttendanceTime,
															 WorkingTimeTypeCategory.Vacation,
															 WorkingTimeTypeCategory.PaidTimeOff,
															 WorkingTimeTypeCategory.UnpaidTimeOff,
															 WorkingTimeTypeCategory.BreakTime,
															 WorkingTimeTypeCategory.Suspension
														 ]) {
		const currentSelection = select.val() as string;

		select.empty();

		this.addUndefinedOption(select, undefinedOption);
		if (items) {
			$.each(order, (index: number, category: WorkingTimeTypeCategory) => {
				const workingTimeTypes = items[category];
				if (workingTimeTypes && workingTimeTypes.length > 0) {
					if (select.children().length > 0) {
						select.append(DOMUtil.createSeparator());
					}
					$.each(workingTimeTypes, (index: number, item: WorkingTimeType) => {
						select.append(createOptionItemCallback(item));
					});
				}
			});
			if (currentSelection && this.hasOption(select, currentSelection)) {
				select.val(currentSelection);
			} else {
				select.val(select.find("option:first").val() as string);
			}
		}
	}

	private static addUndefinedOption(select: JQuery, undefinedOption: [string, string] | boolean) {
		if (typeof undefinedOption === "boolean") {
			if (undefinedOption) {
				select.append(this.createOption("undefined", "---"));
			}
		} else {
			const array = undefinedOption as Array<string>;
			select.append(this.createOption(array[0], array[1]));
		}
	}
}

